import type { JSX } from "solid-js";

// The site's one icon set (design 15 §10, UI review V12): outlines on a 24 px grid, one stroke width, round ends, drawn in the
// words' colour; never a Unicode stand-in, which every platform draws its own way (some as emoji). An icon beside its words is
// hidden from a screen reader; an icon alone takes its control's aria-label. Each shape is drawn afresh where it is used: JSX made
// once is one node, and a second use would move it there.

const SHAPES = {
  home: () => <path d="M4 11.2 12 4.5l8 6.7M6.5 9.5v10h4v-5.5h3v5.5h4v-10" />,
  history: () => (
    <>
      <circle cx="12" cy="12" r="8.25" />
      <path d="M12 7.5V12l3 2" />
    </>
  ),
  board: () => <path d="M7.5 4.5h9v4.25a4.5 4.5 0 0 1-9 0zM7.5 6.25H5.25a2.75 2.75 0 0 0 2.6 3.3M16.5 6.25h2.25a2.75 2.75 0 0 1-2.6 3.3M12 13.25V16.5M8.5 19.5h7M9.75 19.5c0-1.75.95-3 2.25-3s2.25 1.25 2.25 3" />,
  learn: () => <path d="M3 9.25 12 5l9 4.25-9 4.25zM6.75 11v4.4c1.45 1.3 3.2 2.1 5.25 2.1s3.8-.8 5.25-2.1V11M21 9.25v4.5" />,
  rules: () => <path d="M6.25 3.75h8.5l3.5 3.5v13H6.25zM14.75 3.75v3.5h3.5M9 11h6.5M9 14.25h6.5M9 17.5h4" />,
  settings: () => (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 3.5v2.25M12 18.25v2.25M3.5 12h2.25M18.25 12h2.25M6 6l1.6 1.6M16.4 16.4 18 18M6 18l1.6-1.6M16.4 7.6 18 6" />
    </>
  ),
  about: () => (
    <>
      <circle cx="12" cy="12" r="8.25" />
      <path d="M12 11v5.25M12 7.75v.01" />
    </>
  ),
  signout: () => <path d="M14 4.5H6.25v15H14M10.5 12h10M17 8.5l3.5 3.5-3.5 3.5" />,
  menu: () => <path d="M4 7h16M4 12h16M4 17h16" />,
  back: () => <path d="M14.5 6 8.5 12l6 6" />,
  forward: () => <path d="m9.5 6 6 6-6 6" />,
  first: () => <path d="M6.5 5.5v13M17.5 6 9 12l8.5 6z" />,
  last: () => <path d="M17.5 5.5v13M6.5 6l8.5 6-8.5 6z" />,
  play: () => <path d="M8 5.5 18.5 12 8 18.5z" />,
  pause: () => <path d="M9 6v12M15 6v12" />,
  checked: () => (
    <>
      <circle cx="12" cy="12" r="8.25" />
      <path d="m8.25 12.25 2.5 2.5 5-5.25" />
    </>
  ),
  unknown: () => (
    <>
      <circle cx="12" cy="12" r="8.25" />
      <path d="M9.75 9.75a2.25 2.25 0 1 1 3.4 1.95c-.7.4-1.15.9-1.15 1.7v.35M12 16.5v.01" />
    </>
  ),
  alert: () => <path d="M12 4.25 20.5 19H3.5zM12 10v4M12 16.75v.01" />,
  wall: () => <path d="M3.5 7.5h17v9h-17zM3.5 12h17M9.17 7.5v9M14.83 7.5v9" />,
} satisfies Record<string, () => JSX.Element>;

export type IconName = keyof typeof SHAPES;

export function Icon(props: { name: IconName; class?: string }) {
  return (
    <svg
      class={props.class ? `icon ${props.class}` : "icon"}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="1.75"
      stroke-linecap="round"
      stroke-linejoin="round"
      aria-hidden="true"
    >
      {SHAPES[props.name]()}
    </svg>
  );
}
